import { useState } from 'react';
import { Thermometer, AreaChart, Users, Leaf } from 'lucide-react';
import { useTerritoryStore } from '@/stores/territoryStore';
import { useHeatIslands } from '@/hooks/useHeatIslands';
import { isDataUnavailable } from '@/services/dataAvailability';
import { ProductionDataUnavailable } from '@/components/common/ProductionDataUnavailable';
import { IndicatorCard } from '@/components/dashboard/IndicatorCard';
import { ThermalMap } from './ThermalMap';
import { HeatIslandSimulator } from './HeatIslandSimulator';
import type { HeatIslandResult } from './HeatIslandSimulator';

/**
 * IlotsChaleur — écran 4 "Îlots de chaleur (simulation)" — Terravision.
 *
 * Les 4 KPIs sont alimentés par GET /territories/{insee}/heat-islands
 * (hook useHeatIslands). Tant que l'endpoint n'est pas disponible (réseau /
 * 404), on retombe sur des ordres de grandeur de DÉMONSTRATION, clairement
 * signalés par le suffixe « (DEMO) » et le badge global.
 *
 * ⚠ INTÉGRITÉ : la propriété `lst` de heatwave_grid (carte) reste un indice
 * normalisé 0→1, jamais convertie en °C absolus.
 */

// ── Constantes KPI de démonstration (fallback si endpoint indisponible) ───────
// Valeurs typiques pour une commune péri-urbaine dense (~7 km², France tempérée).
// Sources ordre de grandeur : Oke et al. (2017), ADEME (2020), INSEE Filosofi.
const DEMO_DELTA_T_MAX = 6.8;          // °C relatifs (ICU max — DEMO)
const DEMO_OVERHEATED_PCT = 38;        // % surface avec lst > seuil — DEMO
const DEMO_EXPOSED_POPULATION = 7_400; // habitants en zone surchauffe — DEMO
const DEMO_CANOPY_PCT = 14;            // % couvert végétal — DEMO

export function IlotsChaleur() {
  const { activeTerritory } = useTerritoryStore();
  const insee = activeTerritory?.code_insee ?? null;
  const { data: heatIslands, error } = useHeatIslands(insee);
  const [simResult, setSimResult] = useState<HeatIslandResult | null>(null);
  const [showAfter, setShowAfter] = useState(false);

  // Real KPIs when the endpoint answers; DEMO fallback otherwise.
  const isRealKpi = !!heatIslands;
  const demoTag = isRealKpi ? '' : ' (DEMO)';
  // Vrai si les KPI dérivent d'une LST satellite RÉELLE (Landsat), auquel cas
  // le ΔT est en °C mesurés (et non un indice normalisé).
  const isRealLst = heatIslands?._source?.toLowerCase().startsWith('landsat') === true;
  const baseDeltaT = heatIslands?.delta_t_max ?? DEMO_DELTA_T_MAX;
  const overheatedPct = heatIslands?.overheated_pct ?? DEMO_OVERHEATED_PCT;
  const exposedPopulation = heatIslands?.exposed_population ?? DEMO_EXPOSED_POPULATION;
  const canopyPct = heatIslands?.canopy_pct ?? DEMO_CANOPY_PCT;

  // ── Pas de territoire sélectionné ────────────────────────────────────────
  if (!activeTerritory) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted">
          Sélectionnez un territoire pour accéder à la simulation Îlots de chaleur.
        </p>
      </div>
    );
  }


  if (isDataUnavailable(error)) {
    return (
      <ProductionDataUnavailable
        feature="Îlots de chaleur"
        requiredSources="Landsat ou Copernicus LST avec provenance validée"
      />
    );
  }
  // ── ΔT après simulation ───────────────────────────────────────────────────
  // delta_t_total est négatif (refroidissement). On l'ajoute au ΔT max de référence.
  const deltaTCurrent = simResult
    ? Math.max(0, baseDeltaT + simResult.delta_t_total)
    : baseDeltaT;

  // Formatage FR (ex: "4,5") avec signe "+" si positif
  const deltaTFormatted = `${deltaTCurrent > 0 ? '+' : ''}${deltaTCurrent.toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}`;

  return (
    <div className="flex flex-col gap-4 p-5">

      {/* ── En-tête ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[20px] font-extrabold text-ink">
            Îlots de chaleur
          </h2>
          <p className="text-[12.5px] text-faint">
            Simulation de l'atténuation thermique · {activeTerritory.name}
          </p>
        </div>
        {/* Badge DEMO global — affiché uniquement si l'endpoint n'a pas répondu */}
        {!isRealKpi && (
          <span
            className="rounded-full px-3 py-1 text-[11.5px] font-bold"
            style={{ background: '#fff4e5', color: '#ef7d18' }}
            title="KPIs indisponibles côté backend — ordres de grandeur de démonstration"
          >
            DEMO
          </span>
        )}
      </div>

      {/* ── Rangée KPI (4 cards) ─────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3">

        {/* ΔT max — relatif, jamais °C absolus */}
        <IndicatorCard
          label={`ΔT max ICU${demoTag}`}
          value={deltaTFormatted}
          unit="°C rel."
          icon={<Thermometer size={15} />}
          color="#e1000f"
          trend={simResult ? 'down' : 'stable'}
          deltaNote="vs. rural"
        />

        {/* Surface surchauffe */}
        <IndicatorCard
          label={`Surface surchauffe${demoTag}`}
          value={overheatedPct}
          unit="%"
          icon={<AreaChart size={15} />}
          color="#ff5a36"
        />

        {/* Population exposée */}
        <IndicatorCard
          label={`Pop. exposée${demoTag}`}
          value={exposedPopulation}
          unit="hab."
          icon={<Users size={15} />}
          color="#ef7d18"
        />

        {/* Canopée */}
        <IndicatorCard
          label={`Canopée${demoTag}`}
          value={canopyPct}
          unit="%"
          icon={<Leaf size={15} />}
          color="#1f8a5b"
          trend={simResult ? 'up' : 'stable'}
        />
      </div>

      {/* ── Grille 1.55fr / 1fr : ThermalMap | HeatIslandSimulator ──────── */}
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: '1.55fr 1fr', minHeight: '520px' }}
      >
        <ThermalMap
          showAfter={showAfter}
          onShowAfterChange={setShowAfter}
          simDone={simResult !== null}
        />
        <HeatIslandSimulator onResult={setSimResult} />
      </div>

      {/* ── Note d'intégrité des données ─────────────────────────────────── */}
      <p className="pb-1 text-[10.5px] text-faint">
        {isRealKpi ? (
          <>
            <span className="font-semibold">KPIs issus de l'API heat-islands.</span>{' '}
          </>
        ) : (
          <>
            <span className="font-semibold">KPIs de démonstration</span> (endpoint
            heat-islands indisponible).{' '}
          </>
        )}
        {isRealLst ? (
          <>
            ΔT ICU : écart de température de surface RÉELLE (p95−p5), en °C mesurés —
            source {heatIslands?._source}. La carte affiche l'indice thermique normalisé 0→1.
          </>
        ) : (
          <>
            ΔT ICU : différentiel relatif, indice LST normalisé 0→1 — non converti en °C absolus.
            En production : Landsat/Copernicus LST, INSEE carroyage 200 m.
          </>
        )}
      </p>
    </div>
  );
}
